import React, { useState, useEffect, useMemo } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  Button, 
  Card,
  Typography,
  Row,
  Col,
  Upload,
  Space,
  Modal,
  Table,
  Checkbox,
  Tag,
  Spin,
  message,
  Radio,
  Divider,
  Switch,
  AutoComplete
} from 'antd';
import { 
  ArrowLeftOutlined,
  PictureOutlined,
  PlusOutlined,
  CloseOutlined,
  SearchOutlined,
  DeleteOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { adminPaperApi, adminTrophyApi, adminWordSentenceApi, adminFileApi, fileApi } from '@/services/api';
import type { UploadRequestOption as RcUploadRequestOption } from 'rc-upload/lib/interface';
import AdminLayout from '../../../components/Layouts/AdminLayout/AdminLayout';
import UnitTable from '../../../components/admin/UnitTable';
import CreateOptions from '@/pages/admin/components/unit/CreateOptions';
import { formatNumber } from '@/utils/number';

const { Title } = Typography;
const { Option } = Select;

interface TextbookDetail {
  id: string;
  type: string;
  name: string;
  thumbnailUrl?: string;
  registrant: string;
  registrationDate: string;
}

interface Quiz {
  id: string;
  question: string;
  choices: string[];
  answer: number;
  reason: string;
}

interface UnitData {
  id: string;
  type: string;
  name: string;
  registrant: string;
  registrationDate: string;
}

interface AudioSource {
  type: 'word' | 'sentence';
  id: number;
  text: string;
  meaning?: string;
  translation?: string;
  audioUrl: string;
  tts: {
    gender: string;
    age: string;
    speed: string;
    pitch: string;
    voiceName: string;
  };
}

interface Question {
  id: number;
  type: string;
  question: string;
  score: string;
  subjectiveAnswer: string;
  correctAnswer: string | number | number[];
  explanation: string;
  imageUrl: string;
  audioSource: AudioSource | null;
  choices?: string[];
  multiSelect?: boolean;
}

const AdminPaperDetail: React.FC = () => {
  const navigate = useNavigate();
  const { paperId } = useParams<{ paperId: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [textbookData, setTextbookData] = useState<TextbookDetail | null>(null);
  const [unitModalVisible, setUnitModalVisible] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
  const [registeredUnits, setRegisteredUnits] = useState<UnitData[]>([]);
  const [quiz, setQuiz] = useState<Quiz>({
    id: '',
    question: '',
    choices: [],
    answer: 0,
    reason: ''
  });
  const [quizAnswer, setQuizAnswer] = useState<number>(0);
  const [quizChoices, setQuizChoices] = useState<string[]>(['', '', '', '']);
  const [quizReason, setQuizReason] = useState<string>('');
  const [quizQuestion, setQuizQuestion] = useState<string>('');
  const [questionType, setQuestionType] = useState<string>('');
  const [questions, setQuestions] = useState<Question[]>([
    { id: 1, type: 'subjective', question: '', score: '', subjectiveAnswer: '', correctAnswer: '', explanation: '', imageUrl: '', audioSource: null, choices: ['', ''], multiSelect: false }
  ]);
  const [searchOptions, setSearchOptions] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState<{ [key: number]: string }>({});
  const [trophies, setTrophies] = useState<any[]>([]);
  const [trophyConfig, setTrophyConfig] = useState<{
    perfect: { trophyBadgeId: number | null };
    rules: { minScore: number; trophyBadgeId: number | null }[];
  }>({ perfect: { trophyBadgeId: null }, rules: [] });

  const isEditMode = !!paperId && paperId !== 'add';

  // 데이터 로드
  useEffect(() => {
    const loadPaperData = async () => {
      if (isEditMode && paperId) {
        try {
          const res = await adminPaperApi.detail(Number(paperId));
          const data = res?.data || res;
          
          // 기본 정보 설정
          console.log('Loaded aiScoring from DB:', data.aiScoring, typeof data.aiScoring);
          form.setFieldsValue({
            title: data.title,
            price: data.price,
            isPublic: data.isPublic,
            registrant: data.registrant,
            registrationDate: data.registrationDate,
            time: data.timeLimit,
            aiScoring: data.aiScoring ? 'yes' : 'no' // boolean을 'yes'/'no'로 변환
          });

          // 트로피 설정 파싱
          if (data.trophyConfig) {
            try {
              const config = typeof data.trophyConfig === 'string' 
                ? JSON.parse(data.trophyConfig) 
                : data.trophyConfig;
              setTrophyConfig(config);
            } catch (e) {
              console.error('트로피 설정 파싱 실패:', e);
            }
          }

          // 문제 데이터 파싱 (paper_item 테이블에서만 로드)
          let questionsData: any[] = [];
          
          if (data.items && Array.isArray(data.items) && data.items.length > 0) {
            // PaperItem 형식 → Question 형식 변환
            console.log('PaperItem 데이터로부터 로드:', data.items);
            questionsData = data.items.map((item: any, index: number) => {
              const questionType = item.questionType === 'MULTIPLE_CHOICE' ? 'objective' :
                                 item.questionType === 'SHORT_ANSWER' ? 'subjective' :
                                 item.questionType === 'ESSAY' ? 'freestyle' : 'subjective';
              
              // choices와 answer JSON 파싱
              let choices: string[] = [];
              let correctAnswer: any = '';
              let subjectiveAnswer = '';
              let multiSelect = false;
              
              try {
                if (item.choices) {
                  choices = typeof item.choices === 'string' ? JSON.parse(item.choices) : item.choices;
                }
                if (item.answer) {
                  const parsedAnswer = typeof item.answer === 'string' ? JSON.parse(item.answer) : item.answer;
                  if (questionType === 'objective') {
                    if (Array.isArray(parsedAnswer)) {
                      multiSelect = true;
                      correctAnswer = parsedAnswer
                        .map((val: any) => {
                          const num = typeof val === 'number' ? val : parseInt(val, 10);
                          return Number.isNaN(num) ? null : num;
                        })
                        .filter((val: number | null): val is number => val !== null);
                    } else if (typeof parsedAnswer === 'number') {
                      correctAnswer = parsedAnswer;
                    } else if (parsedAnswer !== undefined && parsedAnswer !== null) {
                      const num = parseInt(parsedAnswer, 10);
                      correctAnswer = Number.isNaN(num) ? 0 : num;
                    } else {
                      correctAnswer = 0;
                    }
                  } else {
                    subjectiveAnswer = parsedAnswer; // 주관식: 텍스트
                    correctAnswer = parsedAnswer;
                  }
                }
              } catch (e) {
                console.error('choices/answer 파싱 실패:', e);
              }

              // TTS 파싱
              let parsedTts: any = {
                gender: '',
                age: '',
                speed: '',
                pitch: '',
                voiceName: ''
              };
              try {
                if ((item as any).audioTts) {
                  const ttsData = typeof (item as any).audioTts === 'string'
                    ? JSON.parse((item as any).audioTts)
                    : (item as any).audioTts;
                  
                  console.log(`Question ${item.id} - TTS 데이터:`, ttsData);
                  
                  // TTS 데이터 병합
                  parsedTts = {
                    gender: ttsData.gender || '',
                    age: ttsData.age || '',
                    speed: ttsData.speed || '',
                    pitch: ttsData.pitch || '',
                    voiceName: ttsData.voiceName || ''
                  };
                }
              } catch (e) {
                console.warn('audioTts 파싱 실패:', e);
              }

              // audioText 추출
              const audioTextValue = (item as any).audioText || '';
              
              console.log(`Question ${item.id} - audioUrl: ${item.audioUrl}, audioText: ${audioTextValue}, tts:`, parsedTts);

              return {
                id: item.id || index + 1,
                type: questionType,
                question: item.questionText || '',
                score: item.score?.toString() || '',
                subjectiveAnswer: subjectiveAnswer,
                correctAnswer: correctAnswer,
                explanation: item.explanation || '',
                imageUrl: item.imageUrl || '',
                audioSource: item.audioUrl ? {
                  type: 'word' as const,
                  id: 0,
                  text: audioTextValue, // audioText 필드 사용
                  meaning: '', // DB에 저장 안되므로 빈 문자열
                  translation: '', // DB에 저장 안되므로 빈 문자열
                  audioUrl: item.audioUrl,
                  tts: parsedTts
                } : null,
                choices: questionType === 'objective' && choices.length === 0 ? ['', ''] : choices,
                multiSelect
              };
            });
          } else {
            console.log('paper_item 데이터가 없습니다.');
          }
          
          if (questionsData.length > 0) {
            try {
              // 각 문제의 choices 확인 및 초기화
              const normalizedQuestions = questionsData.map((q: any) => ({
                ...q,
                choices: q.type === 'objective' ? (Array.isArray(q.choices) ? q.choices : ['', '']) : (Array.isArray(q.choices) ? q.choices : []),
                correctAnswer: q.type === 'objective'
                  ? (q.multiSelect
                      ? (Array.isArray(q.correctAnswer) ? q.correctAnswer : [])
                      : (typeof q.correctAnswer === 'number' ? q.correctAnswer : 0))
                  : (q.correctAnswer ?? ''),
                explanation: q.explanation || '',
                multiSelect: q.multiSelect && q.type === 'objective'
              }));
              
              setQuestions(normalizedQuestions);
              
              // 각 문제의 점수와 답안을 폼에 설정
              const newSearchInputValue: { [key: number]: string } = {};
              normalizedQuestions.forEach((q: Question) => {
                console.log(`Loading question ${q.id} (${q.type}):`, {
                  score: q.score,
                  subjectiveAnswer: q.subjectiveAnswer,
                  correctAnswer: q.correctAnswer,
                  explanation: q.explanation,
                  audioSource: q.audioSource
                });
                
                form.setFieldsValue({
                  [`questionType_${q.id}`]: q.type,
                  [`score_${q.id}`]: q.score,
                  [`subjectiveAnswer_${q.id}`]: q.subjectiveAnswer,
                  [`correctAnswer_${q.id}`]: (q.type === 'subjective' || q.type === 'freestyle') ? q.correctAnswer : '',
                  [`explanation_${q.id}`]: q.explanation
                });
                
                // 음원 소스가 있으면 AutoComplete 값 설정 (audioText 사용)
                if (q.audioSource && q.audioSource.text) {
                  console.log(`Question ${q.id} - AutoComplete 입력창 값 설정: "${q.audioSource.text}"`);
                  newSearchInputValue[q.id] = q.audioSource.text;
                }
              });
              console.log('최종 AutoComplete 입력값:', newSearchInputValue);
              setSearchInputValue(newSearchInputValue);
            } catch (e) {
              console.error('문제 데이터 파싱 실패:', e);
            }
          }
        } catch (error) {
          console.error('데이터 로드 실패:', error);
          message.error('데이터를 불러오는데 실패했습니다.');
        }
      }
    };

    loadPaperData();
  }, [isEditMode, paperId, form]);

  // 퀴즈 관련 함수들 (객관식용)
  const handleSetQuizAnswer = (questionId: number, answerIndex: number) => {
    setQuestions(prevQuestions => prevQuestions.map(q => {
      if (q.id !== questionId) return q;
      if (q.type === 'objective' && q.multiSelect) {
        const currentAnswers = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
        const nextAnswers = currentAnswers.includes(answerIndex)
          ? currentAnswers.filter(idx => idx !== answerIndex)
          : [...currentAnswers, answerIndex];
        return { ...q, correctAnswer: nextAnswers };
      }
      return { ...q, correctAnswer: answerIndex };
    }));
  };

  const handleUpdateQuizChoice = (questionId: number, choiceIndex: number, value: string) => {
    console.log('handleUpdateQuizChoice called:', questionId, choiceIndex, value);
    setQuestions(prevQuestions => prevQuestions.map(q => {
      if (q.id === questionId) {
        const newChoices = q.choices ? [...q.choices] : ['', ''];
        newChoices[choiceIndex] = value;
        console.log('Updated choices:', newChoices);
        return { ...q, choices: newChoices };
      }
      return q;
    }));
  };

  const handleRemoveQuizChoice = (questionId: number, choiceIndex: number) => {
    console.log('handleRemoveQuizChoice called:', questionId, choiceIndex);
    setQuestions(prevQuestions => prevQuestions.map(q => {
      if (q.id === questionId && q.choices && q.choices.length > 2) {
        const newChoices = q.choices.filter((_, index) => index !== choiceIndex);
        console.log('Removed choice, new choices:', newChoices);
        return { ...q, choices: newChoices };
      }
      return q;
    }));
  };

  const handleAddQuizChoice = (questionId: number) => {
    console.log('handleAddQuizChoice called:', questionId);
    setQuestions(prevQuestions => prevQuestions.map(q => {
      if (q.id === questionId) {
        const currentChoices = q.choices || ['', ''];
        if (currentChoices.length < 5) {
          const newChoices = [...currentChoices, ''];
          console.log('Added choice, new choices:', newChoices);
          return { ...q, choices: newChoices };
        }
      }
      return q;
    }));
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: questions.length + 1,
      type: 'subjective',
      question: '',
      score: '',
      subjectiveAnswer: '',
      correctAnswer: '',
      explanation: '',
      imageUrl: '',
      audioSource: null,
      choices: ['', ''],
      multiSelect: false
    };
    setQuestions([...questions, newQuestion]);
  };

  const handleSearchWordSentence = async (value: string) => {
    if (!value || value.length < 1) {
      setSearchOptions([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await adminWordSentenceApi.search({ q: value, page: 0, size: 50 });
      const data = res?.data || res;
      const results = data?.results || data?.data?.results || [];
      
      // 각 항목을 개별 옵션으로 표시 (TTS 정보 포함)
      const options = results.map((item: any, index: number) => ({
        value: `${item.type}_${item.id}_${index}`, // 고유한 키 생성
        label: (
          <div>
            <div>
              <span style={{ fontWeight: 'bold' }}>[{item.type === 'word' ? '단어' : '문장'}]</span> {item.text}
            </div>
            <div style={{ fontSize: '12px', color: '#888' }}>
              {item.type === 'word' ? item.meaning : item.translation}
              {' | '}
              TTS: {item.tts?.gender}/{item.tts?.age}/{item.tts?.speed}/{item.tts?.pitch}
            </div>
          </div>
        ),
        data: item
      }));
      setSearchOptions(options);
    } catch (e) {
      console.error('검색 실패:', e);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectWordSentence = (questionId: number, value: string, option: any) => {
    const selectedData = option?.data;
    if (selectedData) {
      setQuestions(questions.map(q => 
        q.id === questionId ? { ...q, audioSource: selectedData } : q
      ));
      // 입력 필드에 표시할 텍스트 업데이트
      setSearchInputValue(prev => ({
        ...prev,
        [questionId]: selectedData.text
      }));
    }
  };

  const findDuplicatePaper = async (title: string) => {
    if (!title?.trim()) return null;
    try {
      const res = await adminPaperApi.list({ q: title.trim(), page: 0, size: 1 });
      const data = res?.data || res;
      const content = data?.content || data?.data?.content || [];
      const matched = content.find((paper: any) => String(paper?.name || paper?.title || '').trim() === title.trim());
      return matched || null;
    } catch (error) {
      console.warn('중복 시험 조회 실패:', error);
      return null;
    }
  };

  // [QA Fix] Row 314 - allow direct audio uploads for speaking questions
  const handleAudioUpload = async (questionId: number, options: RcUploadRequestOption) => {
    const uploadFile = options.file as File;
    try {
      const res = await fileApi.upload(uploadFile, 'paper-audio');
      const url = res?.data?.url || res?.url || '';
      if (!url) {
        throw new Error('음원 URL을 확인할 수 없습니다.');
      }
      setQuestions(prev =>
        prev.map(q => 
          q.id === questionId
            ? {
                ...q,
                audioSource: {
                  type: q.audioSource?.type || 'word',
                  id: q.audioSource?.id || 0,
                  text: uploadFile.name,
                  meaning: '',
                  translation: '',
                  audioUrl: url,
                  tts: q.audioSource?.tts || {
                    gender: '',
                    age: '',
                    speed: '',
                    pitch: '',
                    voiceName: ''
                  }
                }
              }
            : q
        )
      );
      setSearchInputValue(prev => ({ ...prev, [questionId]: uploadFile.name }));
      message.success('음원이 업로드되었습니다.');
      options.onSuccess?.(res, uploadFile);
    } catch (error) {
      console.error('Audio upload failed:', error);
      message.error('음원 업로드에 실패했습니다.');
      options.onError?.(error as Error);
    }
  };

  const handleRemoveAudio = (questionId: number) => {
    setQuestions(prev =>
      prev.map(q => q.id === questionId ? { ...q, audioSource: null } : q)
    );
    setSearchInputValue(prev => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const handleRemoveQuestion = (id: number) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const handleQuestionChange = (id: number, field: string, value: string) => {
    setQuestions(questions.map(q => 
      q.id === id ? { ...q, [field]: value } : q
    ));
  };

  const typeOptions = [
    { value: '파닉스데이', label: '파닉스데이' },
    { value: '스타터_파닉스', label: '스타터 파닉스' },
    { value: '스타터_뉘앙스', label: '스타터 뉘앙스' },
    { value: '스타터_스트럭처', label: '스타터 스트럭처' },
    { value: '에세이', label: '에세이' },
    { value: '통번역', label: '통번역' },
    { value: '리딩북', label: '리딩북' }
  ];

  const questionTypeOptions = [
    { value: 'subjective', label: '주관식' },
    { value: 'objective', label: '객관식 (단일 선택)' },
    { value: 'objective_multi', label: '객관식 (다중 선택)' },
    { value: 'freestyle', label: '프리라이팅' }
  ];

  const totalScore = useMemo(() => {
    return questions.reduce((sum, q) => {
      const numeric = Number(q.score);
      return sum + (Number.isNaN(numeric) ? 0 : numeric);
    }, 0);
  }, [questions]);

  const getQuestionTypeValue = (question: Question) => {
    if (question.type === 'objective' && question.multiSelect) {
      return 'objective_multi';
    }
    return question.type;
  };

  const updateQuestionType = (questionId: number, value: string) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => {
        if (q.id !== questionId) return q;

        if (value === 'objective_multi') {
          const currentAnswer = Array.isArray(q.correctAnswer)
            ? q.correctAnswer
            : typeof q.correctAnswer === 'number'
              ? [q.correctAnswer]
              : [];
          return {
            ...q,
            type: 'objective',
            multiSelect: true,
            correctAnswer: currentAnswer,
            choices: q.choices && q.choices.length > 0 ? q.choices : ['', ''],
          };
        }

        if (value === 'objective') {
          const normalizedAnswer = Array.isArray(q.correctAnswer)
            ? (q.correctAnswer.length > 0 ? q.correctAnswer[0] : 0)
            : (typeof q.correctAnswer === 'number' ? q.correctAnswer : 0);
          return {
            ...q,
            type: 'objective',
            multiSelect: false,
            correctAnswer: normalizedAnswer,
            choices: q.choices && q.choices.length > 0 ? q.choices : ['', ''],
          };
        }

        return {
          ...q,
          type: value,
          multiSelect: false,
        };
      })
    );
    form.setFieldsValue({ [`questionType_${questionId}`]: value });
  };

  const throwValidationError = (messageText: string): never => {
    message.error(messageText);
    throw new Error('validation-error');
  };

  const validateQuestions = (questionList: Question[]) => {
    let totalScore = 0;

    questionList.forEach((q, index) => {
      const displayIndex = index + 1;

      if (!q.question || !q.question.trim()) {
        throwValidationError(`문항 ${displayIndex}: 질문을 입력해주세요.`);
      }

      const numericScore = Number(q.score);
      if (Number.isNaN(numericScore) || numericScore <= 0) {
        throwValidationError(`문항 ${displayIndex}: 유효한 점수를 입력해주세요.`);
      }
      totalScore += numericScore;

      if (q.type === 'objective') {
        const choices = Array.isArray(q.choices) ? q.choices : [];
        if (choices.length < 2) {
          throwValidationError(`문항 ${displayIndex}: 객관식 보기 최소 2개를 입력해주세요.`);
        }
        const hasEmptyChoice = choices.some(choice => !choice || !choice.trim());
        if (hasEmptyChoice) {
          throwValidationError(`문항 ${displayIndex}: 모든 보기 내용을 입력해주세요.`);
        }

        if (q.multiSelect) {
          const answers = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
          if (answers.length === 0) {
            throwValidationError(`문항 ${displayIndex}: 하나 이상의 정답을 선택해주세요.`);
          }
          const invalidAnswer = answers.some(ans => typeof ans !== 'number' || ans < 0 || ans >= choices.length);
          if (invalidAnswer) {
            throwValidationError(`문항 ${displayIndex}: 정답 선택이 올바르지 않습니다.`);
          }
        } else {
          const answerIndex = typeof q.correctAnswer === 'number' ? q.correctAnswer : NaN;
          if (Number.isNaN(answerIndex) || answerIndex < 0 || answerIndex >= choices.length) {
            throwValidationError(`문항 ${displayIndex}: 정답을 선택해주세요.`);
          }
        }

        if (!q.explanation || !q.explanation.trim()) {
          throwValidationError(`문항 ${displayIndex}: 해답을 입력해주세요.`);
        }
      } else {
        if (typeof q.correctAnswer !== 'string' || !q.correctAnswer.trim()) {
          throwValidationError(`문항 ${displayIndex}: 해답을 입력해주세요.`);
        }
      }
    });

    if (totalScore !== 100) {
      throwValidationError('모든 문항 점수의 합은 100점이어야 합니다.');
    }
  };

  // 샘플 데이터 (수정 모드일 때 사용)
  const sampleTextbookData: TextbookDetail = {
    id: 'textbook001',
    type: '파닉스데이',
    name: '파닉스데이 교재 1',
    thumbnailUrl: '',
    registrant: '김관리자',
    registrationDate: '2024-01-15'
  };

  const handleBackToList = () => {
    navigate('/admin/paper');
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      console.log('=== handleSubmit 시작 ===');
      console.log('Form values:', values);
      console.log('Current questions state:', questions);

      if (!isEditMode) {
        const duplicatePaper = await findDuplicatePaper(values.title);
        if (duplicatePaper) {
          setSaving(false);
          Modal.confirm({
            title: '학습이력이 있는 시험입니다. 수정하시겠습니까?',
            content: `'${values.title}' 시험지가 이미 등록되어 있습니다.`,
            okText: '수정하기',
            cancelText: '취소',
            onOk: () => navigate(`/admin/paper/detail/${duplicatePaper.id}`),
          });
          return;
        }
      }
      
      // Form 값을 questions 배열에 반영
      // State를 우선 사용하되, Form 값으로 덮어쓸 수 있음
      const updatedQuestions = questions.map(q => {
        // State 값을 기본으로 사용
        const scoreValue = q.score;
        const subjectiveAnswerValue = q.subjectiveAnswer || '';
        const correctAnswerValue = q.correctAnswer;
        const explanationValue = q.explanation || '';
        
        console.log(`Question ${q.id} (${q.type}):`, {
          scoreValue,
          subjectiveAnswerValue,
          correctAnswerValue,
          explanationValue,
          fromState: {
            score: q.score,
            subjectiveAnswer: q.subjectiveAnswer,
            correctAnswer: q.correctAnswer,
            explanation: q.explanation
          }
        });
        
        return {
          ...q,
          score: scoreValue,
          subjectiveAnswer: subjectiveAnswerValue,
          correctAnswer: correctAnswerValue,
          explanation: explanationValue
        };
      });
      
      console.log('Updated questions:', updatedQuestions);

      console.log('Form aiScoring value:', values.aiScoring, typeof values.aiScoring);

      validateQuestions(updatedQuestions);
      
      // Base64 이미지 업로드 처리
      console.log('=== Base64 이미지 업로드 시작 ===');
      const questionsWithUploadedImages = await Promise.all(
        updatedQuestions.map(async (q) => {
          // imageUrl이 Base64 형식인지 확인
          if (q.imageUrl && q.imageUrl.startsWith('data:image')) {
            try {
              console.log(`Question ${q.id}: Base64 이미지 발견, 업로드 중...`);
              const uploadResult = await adminFileApi.uploadBase64(q.imageUrl, 'paper-images');
              if (uploadResult.success && uploadResult.data?.url) {
                console.log(`Question ${q.id}: 업로드 성공 ->`, uploadResult.data.url);
                return { ...q, imageUrl: uploadResult.data.url };
              } else {
                console.error(`Question ${q.id}: 업로드 실패`, uploadResult);
                return { ...q, imageUrl: null }; // 업로드 실패 시 null
              }
            } catch (error) {
              console.error(`Question ${q.id}: 업로드 에러`, error);
              return { ...q, imageUrl: null }; // 에러 시 null
            }
          }
          return q; // Base64가 아니면 그대로 반환
        })
      );
      console.log('=== Base64 이미지 업로드 완료 ===');
      
      // Question을 PaperItem 형식으로 변환 (Base64는 이미 URL로 변환됨)
      const items = questionsWithUploadedImages.map((q, index) => {
        console.log(`=== Question ${q.id} 저장 데이터 ===`);
        console.log('audioSource:', q.audioSource);
        console.log('audioSource.text:', q.audioSource?.text);
        console.log('audioSource.tts:', q.audioSource?.tts);
        console.log('questionText:', q.question);
        
        return {
          orderIndex: index,
          refType: 'CUSTOM', // 직접 입력 문제
          refId: null,
          questionType: q.type === 'objective' ? 'MULTIPLE_CHOICE' : 
                       q.type === 'subjective' ? 'SHORT_ANSWER' : 
                       q.type === 'freestyle' ? 'ESSAY' : 'SHORT_ANSWER',
          questionText: q.question,
          choices: q.choices && q.choices.length > 0 ? JSON.stringify(q.choices) : null,
          answer: q.type === 'objective' 
            ? JSON.stringify(q.correctAnswer) // 객관식: 정답 인덱스
            : JSON.stringify(q.subjectiveAnswer || q.correctAnswer || ''), // 주관식/프리라이팅: 텍스트
          score: q.score ? Number(q.score) : null,
          explanation: q.explanation || null,
          imageUrl: q.imageUrl || null,
          audioUrl: q.audioSource?.audioUrl || null,
          audioText: q.audioSource?.text || null,
          audioTts: q.audioSource?.tts ? JSON.stringify(q.audioSource.tts) : null
        };
      });
      
      const payload = {
        title: values.title,
        isPublic: !!values.isPublic,
        price: values.price ? Number(String(values.price).replace(/,/g, '')) : undefined,
        registrant: values.registrant || '관리자',
        trophyConfig: JSON.stringify(trophyConfig),
        timeLimit: values.time ? Number(values.time) : undefined,
        aiScoring: values.aiScoring === 'yes', // 'yes' → true, 'no' → false
        items: items, // PaperItem 형식
      };
      
      console.log('=== 저장할 데이터 ===');
      console.log('payload:', JSON.stringify(payload, null, 2));
      console.log('items:', items);
      console.log('items[0]:', items[0]);
      
      let res;
      if (isEditMode && paperId) {
        // 수정 모드
        res = await adminPaperApi.update(Number(paperId), payload);
        message.success('수정되었습니다.');
      } else {
        // 등록 모드
        res = await adminPaperApi.create(payload);
        message.success('등록되었습니다.');
      }
      
      if (res?.success === false) {
        throw new Error(res?.message || '저장 실패');
      }
      
      navigate('/admin/paper');
    } catch (error) {
      if ((error as Error)?.message !== 'validation-error') {
      console.error('저장 실패:', error);
      message.error('저장에 실패했습니다.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/paper');
  };

  const handleDelete = () => {
    Modal.confirm({
      title: '시험지 삭제',
      content: '정말로 이 시험지를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          if (paperId) {
            await adminPaperApi.delete(Number(paperId));
            message.success('삭제되었습니다.');
            navigate('/admin/paper');
          }
        } catch (error: any) {
          console.error('삭제 실패:', error);
          const errorMsg = error?.response?.data?.message || error?.message || '삭제에 실패했습니다.';
          message.error(errorMsg);
        }
      },
    });
  };

  const handleImageUpload = (questionId: number, info: any) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.status === 'done' || info.file.originFileObj) {
      // 이미지 미리보기 URL 생성
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setQuestions(questions.map(q => 
          q.id === questionId ? { ...q, imageUrl: reader.result as string } : q
        ));
      });
      reader.readAsDataURL(info.file.originFileObj);
    }
  };

  const removeImage = () => {
    // 이미지 제거 처리
    console.log('이미지 제거');
  };

  const handleUnitRegistration = () => {
    setUnitModalVisible(true);
  };

  const handleUnitSelect = (selectedUnitIds: string[]) => {
    setSelectedUnits(selectedUnitIds);
  };

  const handleRemoveUnit = (unitId: string) => {
    setRegisteredUnits(prev => prev.filter(unit => unit.id !== unitId));
    message.success('유닛이 제거되었습니다.');
  };

  const handleUnitModalOk = () => {
    setUnitModalVisible(false);
    
    // 선택된 유닛들을 등록된 유닛 목록에 추가
    const sampleUnitList: UnitData[] = [
      {
        id: '1',
        type: '파닉스데이',
        name: '파닉스데이 유닛 1',
        registrant: '김관리자',
        registrationDate: '2024-01-15'
      },
      {
        id: '2',
        type: '스타터_파닉스',
        name: '스타터 파닉스 유닛 1',
        registrant: '이관리자',
        registrationDate: '2024-01-16'
      },
      {
        id: '3',
        type: '뉘앙스',
        name: '뉘앙스 유닛 1',
        registrant: '박관리자',
        registrationDate: '2024-01-17'
      },
      {
        id: '4',
        type: '스트럭처',
        name: '스트럭처 유닛 1',
        registrant: '김관리자',
        registrationDate: '2024-01-18'
      },
      {
        id: '5',
        type: '에세이',
        name: '에세이 유닛 1',
        registrant: '이관리자',
        registrationDate: '2024-01-19'
      }
    ];

    const newRegisteredUnits = sampleUnitList.filter(unit => 
      selectedUnits.includes(unit.id)
    );
    
    setRegisteredUnits(prev => [...prev, ...newRegisteredUnits]);
    setSelectedUnits([]);
    
    message.success(`${newRegisteredUnits.length}개의 유닛이 교재에 등록되었습니다.`);
  };

  const handleUnitModalCancel = () => {
    setUnitModalVisible(false);
    setSelectedUnits([]);
  };

  React.useEffect(() => {
    const loadTrophies = async () => {
      try {
        const res = await adminTrophyApi.list({ type: 'trophy' });
        const data = res?.data || res;
        const content = data?.content || data?.data?.content || [];
        setTrophies(content);
        
        // grade 1 트로피를 100점 기본값으로 자동 설정
        const grade1Trophy = content.find((t: any) => t.grade === 1);
        if (grade1Trophy && !isEditMode) {
          setTrophyConfig(prev => ({
            ...prev,
            perfect: { trophyBadgeId: grade1Trophy.id }
          }));
        }
      } catch (e) {
        console.error('트로피 목록 로드 실패:', e);
      }
    };
    loadTrophies();

    if (isEditMode) {
      // 수정 모드: 데이터 로드
      setLoading(true);
      setTimeout(() => {
        setTextbookData(sampleTextbookData);
        form.setFieldsValue(sampleTextbookData);
        setLoading(false);
      }, 500);
    } else {
      // 등록 모드: 빈 폼으로 시작
      form.setFieldsValue({
        isPublic: true,
        registrant: '관리자',
        aiScoring: 'yes' // AI 채점을 기본값으로 설정
      });
    }
  }, [paperId, form, isEditMode]);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>로딩 중...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList}
            >
              목록으로
            </Button>
          </Space>
          <Title level={2}>
            {isEditMode ? '시험 상세' : '시험 등록'}
          </Title>
        </div>

        
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
              e.preventDefault();
            }
          }}
        >
            <Col span={6}>
              <Form.Item
                label="시험명"
                name="title"
                rules={[
                  { required: true, message: '시험명을 입력해주세요' }
                ]}
              >
                <Input
                  placeholder="시험명을 입력하세요"
                  size="large"
                />
              </Form.Item>
            </Col>
            <p className='mb-2'>트로피 설정</p>
            {/* 100점 고정 */}
            <Row gutter={24}>
                <Col span={6}>
                <div className='flex gap-2 items-end'>
                    <Input
                    value="100"
                    size="large"
                    disabled
                    />
                    <p>점</p>
                </div>
                </Col>
                <Col span={12}>
                    <Select
                    placeholder="트로피를 선택하세요"
                    size="large"
                    value={trophyConfig.perfect.trophyBadgeId}
                    onChange={(v) => setTrophyConfig(prev => ({ ...prev, perfect: { trophyBadgeId: v } }))}
                    style={{ width: '100%' }}
                    >
                      {trophies.map(t => (
                        <Option key={t.id} value={t.id}>{t.name}</Option>
                      ))}
                    </Select>
                </Col>
            </Row>

            {/* 동적 점수 구간 */}
            {trophyConfig.rules.map((rule, idx) => {
              const maxScore = idx === 0 ? 99 : (trophyConfig.rules[idx - 1]?.minScore || 100) - 1;

              return (
                <Row gutter={24} className='my-4' key={idx}>
                  <Col span={6}>
                    <div className='flex gap-2 items-center'>
                      <Input
                        value={rule.minScore}
                        size="large"
                        type="number"
                        min={0}
                        max={maxScore}
                        onChange={(e) => {
                          const val = Number(e.target.value || 0);
                          setTrophyConfig(prev => ({
                            ...prev,
                            rules: prev.rules.map((r, i) => i === idx ? { ...r, minScore: val } : r)
                          }));
                        }}
                      />
                      <span>~</span>
                      <Input
                        value={maxScore}
                        size="large"
                        disabled
                      />
                      <p>점</p>
                    </div>
                  </Col>
                  <Col span={12}>
                    <Select
                      placeholder="트로피를 선택하세요"
                      size="large"
                      value={rule.trophyBadgeId}
                      onChange={(v) => setTrophyConfig(prev => ({
                        ...prev,
                        rules: prev.rules.map((r, i) => i === idx ? { ...r, trophyBadgeId: v } : r)
                      }))}
                      style={{ width: '100%' }}
                    >
                      {trophies.map(t => (
                        <Option key={t.id} value={t.id}>{t.name}</Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={2} className='mt-1.5 '>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => setTrophyConfig(prev => ({
                        ...prev,
                        rules: prev.rules.filter((_, i) => i !== idx)
                      }))}
                    >
                      삭제
                    </Button>
                  </Col>
                </Row>
              );
            })}
            
            <Button 
              type="dashed" 
              onClick={() => setTrophyConfig(prev => ({
                ...prev,
                rules: [...prev.rules, { minScore: 0, trophyBadgeId: null }]
              }))}
              className="mt-1.5 mb-4">
              점수 구간 추가
            </Button>
            
            <Col span={12}>
              <Form.Item
                label="응시 제한시간 (분)"
                name="time"
              >
                <Input
                  placeholder="응시 제한시간을 입력하세요"
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="AI 채점 여부"
                name="aiScoring"
              >
                <Radio.Group>
                    <Radio value="yes">AI 채점</Radio>
                    <Radio value="no">일반 채점</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="시험지 가격"
                name="price"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }
                      const numericValue = Number(String(value).replace(/,/g, ''));
                      if (Number.isNaN(numericValue)) {
                        return Promise.reject(new Error('숫자만 입력해주세요.'));
                      }
                      if (numericValue < 200) {
                        return Promise.reject(new Error('가격은 200원 이상 입력해주세요.'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  placeholder="시험지 가격을 입력하세요"
                  size="large"
                  suffix="원"
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    const formatted = value ? formatNumber(value, '') : '';
                    form.setFieldValue('price', formatted);
                  }}
                />
              </Form.Item>
            </Col>

            <Divider />
            <div className="flex justify-end items-center mb-3 text-sm font-semibold">
              <span>총점:</span>
              <span
                style={{
                  color: totalScore === 100 ? '#16a34a' : '#f5222d',
                  marginLeft: 6
                }}
              >
                {totalScore} / 100
              </span>
            </div>
            <div className='flex flex-col gap-4'>
            {questions.map((question, index) => (
              <Card 
                key={question.id} 
                type="inner"
                title={`문항 ${question.id}`} 
                className="mb-"
                extra={
                  questions.length > 1 && (
                    <Button 
                      type="text" 
                      danger 
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveQuestion(question.id)}
                    >
                      삭제
                    </Button>
                  )
                }
              >
                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item
                      label="문항 유형"
                      name={`questionType_${question.id}`}
                    >
                      <Select
                        placeholder="문항 유형을 선택하세요"
                        value={getQuestionTypeValue(question)}
                        onChange={(value) => updateQuestionType(question.id, value)}
                        size="large"
                      >
                        {questionTypeOptions.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="점수"
                      name={`score_${question.id}`}
                    >
                      <Input
                        placeholder="점수를 입력하세요"
                        value={question.score}
                        onChange={(e) => handleQuestionChange(question.id, 'score', e.target.value)}
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Col span={12}>
                  <Form.Item
                    label="문제 이미지 업로드"
                    name={`thumbnailUrl_${question.id}`}
                  >
                    <Upload
                      name="thumbnail"
                      listType="picture-card"
                      className="thumbnail-uploader"
                      showUploadList={false}
                      action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188"
                      onChange={(info) => handleImageUpload(question.id, info)}
                      style={{ width: '200px', height: '150px' }}
                    >
                      {question.imageUrl ? (
                        <img src={question.imageUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <div className='flex flex-col justify-center items-center rounded-lg' style={{ width: '200px', height: '150px' }}>
                          <PictureOutlined className='text-2xl text-gray-400' />
                          <div className='mt-2 text-gray-400'>문제 이미지 업로드</div>
                        </div>
                      )}
                    </Upload>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item>
                    <AutoComplete
                      placeholder="음원으로 사용할 단어, 문장을 입력하세요"
                      size="large"
                      value={searchInputValue[question.id] || ''}
                      options={searchOptions}
                      onSearch={handleSearchWordSentence}
                      onChange={(value) => setSearchInputValue(prev => ({ ...prev, [question.id]: value }))}
                      onSelect={(value, option) => handleSelectWordSentence(question.id, value, option)}
                      notFoundContent={searchLoading ? <Spin size="small" /> : null}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  {isEditMode && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Upload
                      accept="audio/*"
                      showUploadList={false}
                      customRequest={(options) => handleAudioUpload(question.id, options as RcUploadRequestOption)}
                    >
                      <Button icon={<UploadOutlined />} size="large">
                        음원 업로드
                      </Button>
                    </Upload>
                    {question.audioSource?.audioUrl && (
                      <Button size="large" danger ghost onClick={() => handleRemoveAudio(question.id)}>
                        음원 제거
                      </Button>
                    )}
                  </div>
                  )}
                  {question.audioSource && (
                    <div style={{ marginTop: '-16px', marginBottom: '16px', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        <strong>선택됨:</strong> [{question.audioSource.type === 'word' ? '단어' : '문장'}] {question.audioSource.text}
                      </div>
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '8px' }}>
                        {/* meaning/translation 표시 (있을 경우만) */}
                        {(question.audioSource.type === 'word' && question.audioSource.meaning) && (
                          <>{question.audioSource.meaning}{' | '}</>
                        )}
                        {(question.audioSource.type === 'sentence' && question.audioSource.translation) && (
                          <>{question.audioSource.translation}{' | '}</>
                        )}
                        TTS: {question.audioSource.tts?.gender}/{question.audioSource.tts?.age}/{question.audioSource.tts?.speed}/{question.audioSource.tts?.pitch}
                      </div>
                      {question.audioSource.audioUrl && (
                        <audio 
                          key={question.audioSource.audioUrl}
                          controls 
                          style={{ width: '100%', height: '32px' }}
                        >
                          <source src={question.audioSource.audioUrl} type="audio/mpeg" />
                        </audio>
                      )}
                    </div>
                  )}
                  <Form.Item>
                    <Input.TextArea
                      placeholder="질문을 입력하세요"
                      value={question.question}
                      onChange={(e) => handleQuestionChange(question.id, 'question', e.target.value)}
                      size="large"
                      rows={4}
                    />
                  </Form.Item>

                  {question.type === 'objective' && (
                    <CreateOptions 
                      quiz={{
                        id: `quiz_${question.id}`,
                        choices: question.choices || ['', ''],
                        answer: question.multiSelect
                          ? (Array.isArray(question.correctAnswer) ? question.correctAnswer : [])
                          : (typeof question.correctAnswer === 'number' ? question.correctAnswer : 0)
                      }}
                      allowMultiple={!!question.multiSelect}
                      onSetQuizAnswer={(quizId, answerIndex) => handleSetQuizAnswer(question.id, answerIndex)}
                      onUpdateQuizChoice={(quizId, choiceIndex, value) => handleUpdateQuizChoice(question.id, choiceIndex, value)}
                      onRemoveQuizChoice={(quizId, choiceIndex) => handleRemoveQuizChoice(question.id, choiceIndex)}
                      onAddQuizChoice={(quizId) => handleAddQuizChoice(question.id)}
                    />
                  )}

                  {question.type === 'subjective' && (
                    <>
                      <Form.Item
                        label="주관식 답안"
                        name={`subjectiveAnswer_${question.id}`}
                      >
                        <Input.TextArea
                          placeholder="주관식 답안을 입력하세요"
                          size="large"
                          rows={4}
                          value={question.subjectiveAnswer}
                          onChange={(e) => {
                            handleQuestionChange(question.id, 'subjectiveAnswer', e.target.value);
                            form.setFieldsValue({ [`subjectiveAnswer_${question.id}`]: e.target.value });
                          }}
                        />
                      </Form.Item>
                      
                      <Form.Item
                        label="해답"
                        name={`correctAnswer_${question.id}`}
                      >
                        <Input.TextArea
                          placeholder="해답을 입력하세요"
                          value={typeof question.correctAnswer === 'string' ? question.correctAnswer : ''}
                          onChange={(e) => {
                            handleQuestionChange(question.id, 'correctAnswer', e.target.value);
                            form.setFieldsValue({ [`correctAnswer_${question.id}`]: e.target.value });
                          }}
                          size="large"
                          rows={3}
                        />
                      </Form.Item>
                    </>
                  )}

                  {question.type === 'freestyle' && (
                    <Form.Item
                      label="해답"
                      name={`correctAnswer_${question.id}`}
                    >
                      <Input.TextArea
                        placeholder="해답을 입력하세요"
                        value={typeof question.correctAnswer === 'string' ? question.correctAnswer : ''}
                        onChange={(e) => {
                          handleQuestionChange(question.id, 'correctAnswer', e.target.value);
                          form.setFieldsValue({ [`correctAnswer_${question.id}`]: e.target.value });
                        }}
                        size="large"
                        rows={4}
                      />
                    </Form.Item>
                  )}

                  {question.type === 'objective' && (
                    <Form.Item
                      label="해답"
                      name={`explanation_${question.id}`}
                    >
                      <Input.TextArea
                        placeholder="해답을 입력하세요"
                        value={question.explanation}
                        onChange={(e) => {
                          handleQuestionChange(question.id, 'explanation', e.target.value);
                          form.setFieldsValue({ [`explanation_${question.id}`]: e.target.value });
                        }}
                        size="large"
                        rows={4}
                      />
                    </Form.Item>
                  )}
                </Col>
              </Card>
            ))}
            </div>
            
            <Button 
              type="primary" 
              size="large" 
              icon={<PlusOutlined />} 
              className='mt-4 mb-4' 
              onClick={handleAddQuestion}
            >
              문항 추가
            </Button>

            <Col span={12} className='mt-4'>
                <Form.Item
                  label="공개설정"
                  name="isPublic"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="공개"
                    unCheckedChildren="미공개"
                  />
                </Form.Item>
            </Col>
  

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="등록자"
                name="registrant"
              >
                <Input
                  disabled
                  size="large"
                />
              </Form.Item>
            </Col>
            {isEditMode && (
              <Col span={12}>
                <Form.Item
                  label="등록일"
                  name="registrationDate"
                >
                  <Input
                    disabled
                    size="large"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* 버튼 영역 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
            {isEditMode && (
              <Button 
                danger
                onClick={handleDelete}
                size="large"
                style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              >
                삭제
              </Button>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <Space size="middle">
                <Button 
                  onClick={handleCancel}
                  size="large"
                  style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                >
                  취소
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={saving}
                  size="large"
                  style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                >
                  {isEditMode ? '수정' : '등록'}
                </Button>
              </Space>
            </div>
          </div>
        </Form>

        {/* 유닛 등록 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>유닛 등록</span>
              <span style={{ fontSize: '14px', color: '#666' }}>
                ({selectedUnits.length}개 선택)
              </span>
            </div>
          }
          open={unitModalVisible}
          onOk={handleUnitModalOk}
          onCancel={handleUnitModalCancel}
          width={1000}
          okText="등록"
          cancelText="취소"
        >
          <div style={{ padding: '20px 0' }}>
            <UnitTable
              showCheckbox={true}
              showSearch={true}
              maxHeight="400px"
              size="small"
              pagination={false}
              selectedUnits={selectedUnits}
              onUnitSelect={handleUnitSelect}
            />
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminPaperDetail;